import React from 'react'
import * as rn from 'react-native'

import * as c from '../../../../common'
import * as r from '../../../../react-utils'

import * as m from '@mui/material'
import * as mi from '@mui/icons-material'
import Entypo from 'react-native-vector-icons/Entypo'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import MaterialIcons from 'react-native-vector-icons/MaterialIcons'
import { useParams } from 'react-router-dom'

import * as cpm from '../../../../app/components'
import * as cp from '../../../comps'
import * as gs from '../../../../app/gStyles'

//@ts-ignore
import styles from './LoanOptions.module.css'

export default React.memo(function LoanOptions() {
  //#region global
  const { customerID } = useParams()
  const totalCost = r.useTotalCost('company', customerID!, true)
  const [solarCompany] = r.useCustomerField(customerID!, 'solarCompany')
  const companyColor = c.companyColors[solarCompany]
  //#endregion global
  //#region local
  const {
    availLoanCompaniesNames,
    loanCompany,
    loanCompanyName,
    loanCompanyNameHandle,

    availOffers,
    offer,
    offerName,
    offerNameHandle,

    availTerms,
    term,
    termName,
    termNameHandle: termNameSet,

    availPlans,
    plan,
    planName,
    planNameHandle,
  } = r.usePayment(customerID!)
  const [offerNameOpen, , offerNameToggle] = r.useBool(false)
  const [inputtedCost, setInputtedCost, handleInputtedCost] = r.useInputVal(
    totalCost.toString(),
  )
  React.useEffect((): void => {
    if (totalCost) {
      setInputtedCost(totalCost.toString())
    }
  }, [setInputtedCost, totalCost])
  const inputtedCostN = Number(inputtedCost)
  const resetCost = React.useCallback(
    () => void setInputtedCost(totalCost.toString()),
    [setInputtedCost, totalCost],
  )

  const totalLoanAmt = inputtedCostN / (1 - (term?.cost || 0))

  const payPerMonth = React.useMemo((): number => {
    if (!term) return 0
    // Branch on the RATE, not on the offer's name. `calcLoanMonthlyPay` is the
    // amortisation formula, whose denominator is `(1 + r)^n - 1` — at r = 0
    // that is 0/0, and `NaN.toFixed(0)` prints the literal string "NaN" where
    // the monthly payment goes, on a page the customer reaches through the
    // signing link (it is only dimmed there, not hidden).
    //
    // Matching `'No interest'` only ever covered GoodLeap. The nine live
    // Synchrony plans carrying `interest: 0` are named "Deferred Interest"
    // (6/12/15/18/24 months) and "Equal Payments (0% APR)" (3/4/5/6 years),
    // so every one of them fell through to the formula. A rate test also
    // covers any future 0% offer whatever someone names it in Item Prices.
    if (!term.interest) {
      return totalLoanAmt / term.years / 12
    }

    return c.calcLoanMonthlyPay(totalLoanAmt, term.interest * 100, term.years)
  }, [term, totalLoanAmt])
  //#endregion local
  //#region styling
  const buttonStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': {
        backgroundColor: c.companyColors[solarCompany],
      },
    }),
    [solarCompany],
  )
  //#endregion styling

  const [justCopied, setJustCopied] = React.useState(false)
  const disableJustCopied = React.useCallback(
    () => void setJustCopied(false),
    [],
  )
  const copyTotalLoanAmt = React.useCallback((): void => {
    rn.Clipboard.setString(totalLoanAmt.toFixed(2).toString())
    setJustCopied(true)
  }, [totalLoanAmt])

  return (
    <div style={sx.column}>
      <div style={sx.inputtedCost}>
        <m.FormControl fullWidth>
          <m.InputLabel htmlFor="outlined-adornment-amount">
            Total Cost
          </m.InputLabel>
          <m.OutlinedInput
            className={styles['total-cost-input']}
            id="outlined-adornment-amount"
            label="Total Cost"
            onChange={handleInputtedCost}
            startAdornment={startAdornment}
            // sx={gs.width100}
            type="number"
            value={inputtedCost}
          />
        </m.FormControl>

        <mi.Replay sx={sx.replayIcon} onClick={resetCost} />
      </div>

      <cpm.Pad amt={50} />

      {/* I would have used a Material UI select but a bug in that library (sigh) mixes up the dropdowns from 2 separate selects for some reason. */}
      <select onChange={loanCompanyNameHandle} value={loanCompanyName}>
        {!loanCompanyName && (
          <option disabled value="">
            Select a company...
          </option>
        )}
        {availLoanCompaniesNames.map((lc) => (
          <option key={lc} value={lc}>
            {lc}
          </option>
        ))}
      </select>

      <cpm.Pad amt={16} />

      {/* Nothing below the company select is reserved until it has content:
          invisible placeholders here held a ~350px hole above Cash/Financing
          when no company was picked (Web QA, 2026-10-17). */}
      {!!loanCompanyName && (
        <rn.Pressable style={gs.rowCentered}>
          <MaterialCommunityIcons
            color={offer ? companyColor : c.light.canvas.opaque}
            name={offer?.icon || 'form-dropdown'}
            size={64}
          />
          {gs.colGap8}
          <cpm.Text
            style={{
              alignSelf: 'flex-end',
              color: offerName ? c.light.canvas.color : c.light.canvas.opaque,
              fontFamily: 'Manrope',
              fontSize: 32,
            }}
          >
            {offerName || 'Select an offer...'}
          </cpm.Text>
          {gs.colGap8}
          <Entypo
            color={c.light.canvas.opaque}
            name="select-arrows"
            size={18}
            style={gs.alignSelfEnd}
          />
        </rn.Pressable>
      )}

      {!!loanCompanyName && (
        <m.Select
          onChange={offerNameHandle}
          onClose={offerNameToggle}
          onOpen={offerNameToggle}
          open={offerNameOpen}
          style={sx.select}
          value={offerName}
        >
          {availOffers.map(([availOfferName, availOffer]) => (
            <m.MenuItem
              key={availOfferName}
              style={sx.menuItem}
              value={availOfferName}
            >
              <cpm.View style={gs.rowCentered}>
                <MaterialCommunityIcons
                  color={c.light.canvas.opaque}
                  name={availOffer.icon}
                  size={18}
                />

                {gs.colGap8}
                <cpm.Text colorScheme="light" size={18}>
                  {availOfferName}
                </cpm.Text>
              </cpm.View>
              {gs.rowGap8}
              <cpm.View style={gs.rowCentered}>
                <MaterialCommunityIcons
                  color={c.light.canvas.opaque}
                  name="abjad-arabic"
                  size={18}
                  // TODO: ??
                  style={gs.opacity0}
                />
                {gs.colGap8}
                <cpm.Text colorScheme="light" size={16}>
                  {availOffer.shortDesc}
                </cpm.Text>
              </cpm.View>
            </m.MenuItem>
          ))}
        </m.Select>
      )}

      {!!offer && (
        <>
          {gs.rowGap24}

          <cpm.View style={sx.headingRow}>
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
            {gs.colGap8}
            <cpm.Text
              style={{
                color: c.light.canvas.color,
                fontFamily: c.light.canvas.fontFamily,
                fontSize: 14,
              }}
            >
              How it works
            </cpm.Text>
            {gs.colGap8}
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
          </cpm.View>

          {gs.rowGap12}

          <cpm.View style={sx.description}>
            <cpm.Text centerText colorScheme="light" size={16}>
              {offer?.desc}
            </cpm.Text>
          </cpm.View>

          {gs.rowGap24}

          <cpm.View>
            {offer.bullets.map((bullet, i) => (
              <React.Fragment key={bullet.text}>
                {i > 0 && gs.rowGap12}
                <cpm.View style={gs.rowCentered}>
                  <MaterialCommunityIcons
                    color={companyColor}
                    name={bullet.icon}
                    size={26}
                  />
                  {gs.colGap8}
                  <cpm.Text colorScheme="light" on="canvas" size={14}>
                    {bullet.text}
                  </cpm.Text>
                </cpm.View>
              </React.Fragment>
            ))}

            {offer.bullets.length === 2 && (
              // Always have the bullet list take up the same height
              <>
                {gs.rowGap12}

                <cpm.View style={gs.rowCenteredOpacity0}>
                  <MaterialCommunityIcons
                    color={companyColor}
                    name="bank"
                    size={26}
                  />
                  {gs.colGap8}
                  <cpm.Text colorScheme="light" on="canvas" size={14}>
                    {'[[SHOULD NOT BE VISIBLE]]'}
                  </cpm.Text>
                </cpm.View>
              </>
            )}
          </cpm.View>

          {gs.rowGap24}
        </>
      )}

      {availPlans.length > 0 && (
        <>
          <cpm.View style={sx.headingRow}>
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
            {gs.colGap8}
            <cpm.Text
              style={{
                color: c.light.canvas.color,
                fontFamily: c.light.canvas.fontFamily,
                fontSize: 14,
              }}
            >
              Choose plan
            </cpm.Text>
            {gs.colGap8}
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
          </cpm.View>

          {gs.rowGap24}

          <cpm.View style={sx.terms}>
            {availPlans.map(([availPlanName]) => (
              <rn.Pressable
                key={availPlanName}
                onPress={() => {
                  planNameHandle(availPlanName)
                }}
                style={{
                  backgroundColor: companyColor,
                  borderRadius: 8,
                  marginHorizontal: 12,
                  padding: 12,
                }}
              >
                <cpm.Text bold centerText colorScheme="dark">
                  {availPlanName}
                </cpm.Text>

                {planName === availPlanName && (
                  <cpm.View
                    style={{
                      ...gs.absolute,
                      ...gs.deadCenter,
                      backgroundColor: '#FFF',
                      borderTopLeftRadius: 4,
                      bottom: 0,
                      padding: 1,
                      right: 0,
                    }}
                  >
                    <MaterialCommunityIcons
                      color={companyColor}
                      name="check"
                      size={12}
                    />
                  </cpm.View>
                )}
              </rn.Pressable>
            ))}
          </cpm.View>
        </>
      )}

      {/* Nothing is reserved for the terms until a plan offers some: an
          invisible placeholder here left a ~190px hole above Cash/Financing
          when no plan was selected (Jaron's iPad, Shinny Test). */}
      {availTerms.length > 0 && (
        <>
          {gs.rowGap24}

          <cpm.View
            style={availTerms.length > 0 ? sx.headingRow : sx.headingRowHidden}
          >
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
            {gs.colGap8}
            <cpm.Text
              style={{
                color: c.light.canvas.color,
                fontFamily: c.light.canvas.fontFamily,
                fontSize: 14,
              }}
            >
              Choose terms
            </cpm.Text>
            {gs.colGap8}
            <cpm.View
              backgroundColor={c.light.canvas.opaque}
              height={1}
              flexGrow={1}
            />
          </cpm.View>

          {gs.rowGap12}

          <cpm.View style={availTerms.length > 0 ? sx.terms : gs.opacity0}>
            {availTerms.map(([availTermName, availTerm]) => (
              <rn.Pressable
                key={availTermName}
                onPress={() => {
                  termNameSet(availTermName)
                }}
                style={{
                  backgroundColor: companyColor,
                  borderRadius: 8,
                  marginHorizontal: 12,
                  padding: 12,
                }}
              >
                <cpm.Text bold centerText colorScheme="dark">
                  {availTerm.years}yr / {(availTerm.interest * 100).toFixed(2)}%
                </cpm.Text>

                {termName === availTermName && (
                  <cpm.View
                    style={{
                      ...gs.absolute,
                      ...gs.deadCenter,
                      backgroundColor: '#FFF',
                      borderTopLeftRadius: 4,
                      bottom: 0,
                      padding: 1,
                      right: 0,
                    }}
                  >
                    <MaterialCommunityIcons
                      color={companyColor}
                      name="check"
                      size={12}
                    />
                  </cpm.View>
                )}
              </rn.Pressable>
            ))}
          </cpm.View>

          {gs.rowGap32}
        </>
      )}

      {/* Selected loan only exists once a term is picked, for the same reason. */}
      {!!term && (
        <>
          <cpm.View style={sx.description}>
            <cpm.View style={term ? sx.headingRow : sx.headingRowHidden}>
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                flexGrow={1}
              />
              {gs.colGap8}
              <cpm.Text
                style={{
                  color: c.light.canvas.color,
                  fontFamily: c.light.canvas.fontFamily,
                  fontSize: 14,
                }}
              >
                Selected loan
              </cpm.Text>
              {gs.colGap8}
              <cpm.View
                backgroundColor={c.light.canvas.opaque}
                height={1}
                flexGrow={1}
              />
            </cpm.View>

            {gs.rowGap12}

            <cpm.Text centerText colorScheme="light" size={18}>
              {term?.desc}
            </cpm.Text>
          </cpm.View>

          <cpm.View
            alignItems="flex-end"
            style={!term && gs.opacity0}
            width="90%"
          >
            <rn.View style={gs.rowCentered}>
              <MaterialIcons
                color={companyColor}
                name="content-copy"
                onPress={copyTotalLoanAmt}
                size={14}
              />
              {gs.colGap8}
              <cpm.Text colorScheme="light" size={10}>
                {/* `money` formats whatever it is given, so NaN would print
                    "$NaN" on a page the customer reaches through the signing
                    link. Same guard the chart above already uses. */}
                Total loan amount:{' '}
                {c.money(Number.isFinite(totalLoanAmt) ? totalLoanAmt : 0)}
              </cpm.Text>
            </rn.View>
            <cpm.Text colorScheme="light" size={10}>
              Dealer fee: {((term?.cost || 0) * 100).toFixed(2)}%
            </cpm.Text>
            <cpm.Text colorScheme="light" size={10}>
              APR: {((term?.interest || 0) * 100).toFixed(2)}%
            </cpm.Text>
            {term?.loanCode && (
              <cpm.Text colorScheme="light" size={10}>
                Loan code: {term?.loanCode}
              </cpm.Text>
            )}
            {term?.minAmt && (
              <cpm.Text colorScheme="light" size={10}>
                Min amt:
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'currency',
                }).format(term?.minAmt)}
              </cpm.Text>
            )}
            {term?.maxAmt && (
              <cpm.Text colorScheme="light" size={10}>
                Max amt:
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  style: 'currency',
                }).format(term?.maxAmt)}
              </cpm.Text>
            )}
            {loanCompany?.contractorID && (
              <cpm.Text colorScheme="light" size={10}>
                Contractor/Dealer #: {loanCompany?.contractorID}
              </cpm.Text>
            )}
            {loanCompany?.programPhoneNumber && (
              <cpm.Text colorScheme="light" size={10}>
                Program phone number: {loanCompany?.programPhoneNumber}
              </cpm.Text>
            )}
          </cpm.View>
        </>
      )}

      {gs.rowGap24}

      <cpm.View
        backgroundColor={c.light.canvas.separator}
        height={2}
        width="100%"
      />

      <cpm.View
        justifyContent="center"
        flexDirection="row"
        flexGrow={1}
        width="100%"
      >
        <cpm.View
          alignItems="center"
          borderRightColor={c.light.canvas.separator}
          borderRightWidth={1}
          paddingHorizontal={12}
          paddingTop={12}
          width="50%"
        >
          <cpm.Text style={sx.title}>Cash</cpm.Text>

          {gs.rowGap16}

          <MaterialCommunityIcons color={companyColor} name="cash" size={56} />

          {gs.rowGap16}

          <cpm.Text colorScheme="light" size={24}>
            {c.money(Number.isFinite(inputtedCostN) ? inputtedCostN : 0)}
          </cpm.Text>
        </cpm.View>

        <cpm.View
          alignItems="center"
          borderLeftColor={c.light.canvas.separator}
          borderLeftWidth={1}
          paddingHorizontal={12}
          paddingTop={12}
          width="50%"
        >
          <cpm.Text style={sx.title}>Financing</cpm.Text>

          {gs.rowGap16}

          <MaterialCommunityIcons color={companyColor} name="bank" size={56} />

          {gs.rowGap32}

          <cpm.Text colorScheme="light" centerText size={24}>
            {/* Combines two independent fixes to the same line: Luis's
                2-decimal fix (565d6849, 17 sep) and the thousand-separator
                fix from the 15 sep bug-hunt (#20, same family as the SHOW
                COMPARISON fix 13020768561) - a short-term/high-cost loan
                can round to 4+ digits a month (e.g. a $50k system over 3
                years), which needs both the comma and the cents. */}
            $
            {payPerMonth.toLocaleString('en', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}{' '}
            / mo
          </cpm.Text>
          <cpm.Text centerText colorScheme="light" size={8}>
            (Estimate)
          </cpm.Text>

          {offer?.choicesInMonths && (
            <>
              {gs.rowGap32}

              <cpm.View
                backgroundColor={c.light.canvas.separator}
                height={2}
                width="100%"
              />

              {gs.rowGap16}

              <cpm.Text style={sx.title}>
                The first {plan?.months} months
              </cpm.Text>

              {gs.rowGap16}

              <MaterialCommunityIcons
                color={companyColor}
                name="calendar"
                size={56}
              />

              {gs.rowGap16}

              <cpm.Text colorScheme="light">You can choose either:</cpm.Text>

              {gs.rowGap16}

              <cpm.Text colorScheme="light">
                {offer?.choicesInMonths[0]}
              </cpm.Text>

              <cpm.View style={gs.rowCentered}>
                <cpm.View
                  backgroundColor={c.light.canvas.separator}
                  height={2}
                  width={64}
                />
                <cpm.Text colorScheme="light">Or</cpm.Text>
                <cpm.View
                  backgroundColor={c.light.canvas.separator}
                  height={2}
                  width={64}
                />
              </cpm.View>

              <cpm.Text colorScheme="light">
                {offer?.choicesInMonths[1]}
              </cpm.Text>
            </>
          )}
        </cpm.View>
      </cpm.View>

      {gs.rowGap24}

      <m.Button
        onClick={handleOpenQualifyTool}
        sx={buttonStyle}
        variant="contained"
      >
        Qualify Tool
      </m.Button>

      <cp.ToastAlert
        duration={2000}
        handleClose={disableJustCopied}
        message="Copied to Clipboard"
        open={justCopied}
        severity="info"
        type="alert"
      />
    </div>
  )
})

const lenSx = {
  borderRadius: '10px',
  paddingLeft: '10px',
  paddingRight: '10px',
  color: '#fff',
}

const sx = {
  /**
   * Jaron, Slack 2026-09-23 21:41 (Monday 12790300968): on iPad/Safari the
   * content sat in a narrow off-centre column, the section dividers were
   * shorter than the rows under them and a stray short rule hung below the
   * description. Everything now lives in ONE centred column: dividers flex to
   * fill it, the description and button rows use its full width.
   */
  column: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
    maxWidth: 720,
    width: '100%',
  } as const,
  description: { width: '100%' } as const,
  headingRow: { ...gs.rowCentered, width: '100%' } as const,
  headingRowHidden: { ...gs.rowCenteredOpacity0, width: '100%' } as const,
  interest: { width: '100px' },
  inputtedCost: { ...gs.rowSpaceBetween100, position: 'relative' } as const,
  len: lenSx,
  lenSelected: { ...lenSx, backgroundColor: '#6C757D' },
  listSubheader: {
    ...gs.userSelectNone,
    alignItems: 'center',
  },
  menuItem: {
    alignItems: 'stretch',
    flexDirection: 'column',
    paddingBottom: 24,
    paddingTop: 24,
  } as const,
  replayIcon: {
    cursor: 'pointer',
    fontSize: '29px',
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
  },
  select: { ...gs.width72, height: 0, opacity: 0 },
  selectCompany: { ...gs.width24, height: 0, opacity: 0 },
  // Plans and terms: rows that wrap inside the white card. Five Synchrony plans
  // on one row ran past it on the iPad (Jaron, Nov 4, 12790300968).
  terms: {
    ...gs.rowWrap,
    //@ts-ignore
    gap: 16,
    justifyContent: 'center' as const,
  },
  title: {
    fontFamily: c.light.fontFamily,
    fontSize: 36,
    textAlign: 'center',
  } as const,
}

const startAdornment = <m.InputAdornment position="start">$</m.InputAdornment>

const handleOpenQualifyTool = () => {
  window.open('https://ecohome.pre-fi.com/pre-qualification', '_blank')?.focus()
}
